import React, { useMemo } from 'react';

interface RhythmData {
  day: number;
  hour: 'morning' | 'afternoon' | 'evening' | 'night';
  value: number;
  count: number;
}

interface WeeklyRhythmChartProps {
  data: RhythmData[];
  language?: 'Italian' | 'English' | 'Spanish' | 'German' | 'French';
  translations?: {
    title: string;
    subtitle: string;
    bestMoment: string;
    levelLow: string;
    levelMedium: string;
    levelHigh: string;
  };
}

const dayLabels = {
  Italian: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'],
  English: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  Spanish: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
  German: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
  French: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
};

const timeLabels = {
  Italian: { morning: 'Mattina', afternoon: 'Pomeriggio', evening: 'Sera', night: 'Notte' },
  English: { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening', night: 'Night' },
  Spanish: { morning: 'Mañana', afternoon: 'Tarde', evening: 'Noche', night: 'Noche' },
  German: { morning: 'Morgen', afternoon: 'Nachmittag', evening: 'Abend', night: 'Nacht' },
  French: { morning: 'Matin', afternoon: 'Après-midi', evening: 'Soir', night: 'Nuit' }
};

const getColorIntensity = (value: number): string => {
  if (value >= 80) return 'bg-emerald-500';
  if (value >= 60) return 'bg-emerald-400';
  if (value >= 40) return 'bg-amber-400';
  if (value >= 20) return 'bg-orange-400';
  if (value > 0) return 'bg-rose-400';
  return 'bg-gray-100';
};

const getOpacity = (count: number): number => {
  if (count === 0) return 0.2;
  if (count === 1) return 0.6;
  if (count <= 3) return 0.8;
  return 1;
};

const defaultTranslations = {
  title: 'Your Rhythm',
  subtitle: 'When you feel best',
  bestMoment: 'Best time',
  levelLow: 'Low',
  levelMedium: 'Medium',
  levelHigh: 'High'
};

const WeeklyRhythmChart: React.FC<WeeklyRhythmChartProps> = ({
  data,
  language = 'Italian',
  translations = defaultTranslations
}) => {
  const days = dayLabels[language];
  const times = timeLabels[language];
  const timeSlots: Array<'morning' | 'afternoon' | 'evening' | 'night'> = ['morning', 'afternoon', 'evening', 'night'];

  const gridData = useMemo(() => {
    const grid: Record<string, { value: number; count: number }> = {};
    
    for (let day = 0; day < 7; day++) {
      for (const hour of timeSlots) {
        grid[`${day}-${hour}`] = { value: 0, count: 0 };
      }
    }
    
    data.forEach(item => {
      const key = `${item.day}-${item.hour}`;
      if (grid[key]) {
        grid[key] = { value: item.value, count: item.count };
      }
    });
    
    return grid;
  }, [data]);

  const bestTime = useMemo(() => {
    let best = { day: 0, hour: 'morning' as const, value: 0 };
    Object.entries(gridData).forEach(([key, { value, count }]) => {
      if (value > best.value && count > 0) {
        const [day, hour] = key.split('-');
        best = { day: parseInt(day), hour: hour as any, value };
      }
    });
    return best;
  }, [gridData]);

  return (
    <div className="bg-white rounded-[24px] p-4 md:p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 md:mb-6">
        <div>
          <h3 className="text-[11px] font-bold text-black/40 uppercase tracking-wider mb-1">{translations.title}</h3>
          <p className="text-[16px] md:text-[18px] font-black text-black">{translations.subtitle}</p>
        </div>
        {bestTime.value > 0 && (
          <div className="sm:text-right">
            <p className="text-[10px] md:text-[11px] text-black/40 font-medium">{translations.bestMoment}</p>
            <p className="text-[13px] md:text-[14px] font-bold text-emerald-600">
              {days[bestTime.day]} {times[bestTime.hour]}
            </p>
          </div>
        )}
      </div>

      <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
        <div className="min-w-[280px]">
          <div className="grid grid-cols-8 gap-1 md:gap-1.5">
            <div className="h-6 md:h-8" />
            {days.map((day, i) => (
              <div key={i} className="h-6 md:h-8 flex items-center justify-center">
                <span className="text-[9px] md:text-[10px] font-bold text-black/40">{day}</span>
              </div>
            ))}

            {timeSlots.map((hour) => (
              <React.Fragment key={hour}>
                <div className="h-10 md:h-12 flex items-center pr-1 md:pr-2">
                  <span className="text-[9px] md:text-[10px] font-bold text-black/30 truncate">
                    {times[hour]}
                  </span>
                </div>
                {[0, 1, 2, 3, 4, 5, 6].map((day) => {
                  const cellData = gridData[`${day}-${hour}`];
                  return (
                    <div
                      key={`${day}-${hour}`}
                      className={`h-10 md:h-12 rounded-lg md:rounded-xl ${getColorIntensity(cellData.value)} transition-all duration-300 hover:scale-110 cursor-default group relative`}
                      style={{ opacity: getOpacity(cellData.count) }}
                    >
                      {cellData.count > 0 && (
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="text-[9px] md:text-[10px] font-bold text-white drop-shadow-sm">
                            {cellData.value}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-3 md:gap-4 mt-4 md:mt-6 pt-3 md:pt-4 border-t border-black/5">
        <div className="flex items-center gap-1 md:gap-1.5">
          <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded bg-rose-400" />
          <span className="text-[9px] md:text-[10px] font-medium text-black/40">{translations.levelLow}</span>
        </div>
        <div className="flex items-center gap-1 md:gap-1.5">
          <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded bg-amber-400" />
          <span className="text-[9px] md:text-[10px] font-medium text-black/40">{translations.levelMedium}</span>
        </div>
        <div className="flex items-center gap-1 md:gap-1.5">
          <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded bg-emerald-500" />
          <span className="text-[9px] md:text-[10px] font-medium text-black/40">{translations.levelHigh}</span>
        </div>
      </div>
    </div>
  );
};

export default WeeklyRhythmChart;
